import { IExcelReportData, IRowCells, ITableData } from 'types/excelExport';
import {
    createBlankRow,
    createDarkSectionHeader,
    createDataRow,
    createHeaderRow,
    createLabelValueRow,
    formatDate,
    formatNumber,
    parseNumber,
} from '../helpers';
import { EXCEL_COLORS, EXCEL_NUM_FORMATS } from '../constants';
import { LD_EXCEL_SHEET_NAMES, LD_LABELS } from './constants';
import { formatLdSalesStart } from './salesStartDisplay';
import { ILdDispositionSheetParams } from './types';

const createSectionHeader = createDarkSectionHeader;

const formatLotName = (lotName: string | null, lotWidth: number): string => (
    lotName || `${formatNumber(lotWidth)} ft`
);

const formatSaleFrequency = (frequency: string | undefined): string => {
    if (frequency === 'monthly') return 'Monthly';
    if (frequency === 'quarterly') return 'Quarterly';
    return frequency ?? '';
};

const DISPOSITION_WIDE_COL_COUNT = 6;
const DISPOSITION_PRICING_COL_COUNT = 6;
const DISPOSITION_TAKEDOWN_COL_COUNT = 4;

const createWideSpacerCells = (count: number = DISPOSITION_WIDE_COL_COUNT - 2): IRowCells[] => (
    Array(count).fill(null).map(() => ({ value: '', width: 12 }))
);

const createWideAmountCell = (
    value: number,
    currencyFmt: string,
    bold: boolean = false,
): IRowCells => ({
    value,
    width: 18,
    alignment: { horizontal: 'center' },
    numFmt: currencyFmt,
    cellFont: bold ? { bold: true } : undefined,
});

const computeBlendedPricing = (
    mixes: NonNullable<ILdDispositionSheetParams['residentialLotMixes']>['residentialLotMixes'],
) => {
    const totalLots = mixes.reduce((sum, m) => sum + m.lotCount, 0);
    if (totalLots === 0) {
        return { costPerFrontFoot: null, pricePerLot: null, lotCount: 0, totalGrossProceeds: 0 };
    }
    const weightedFrontFoot = mixes.reduce(
        (sum, m) => sum + (parseNumber(m.costPerFrontFoot) ?? 0) * m.lotCount,
        0,
    ) / totalLots;
    const totalGrossProceeds = mixes.reduce(
        (sum, m) => sum + (parseNumber(m.pricePerLot) ?? 0) * m.lotCount,
        0,
    );
    return {
        costPerFrontFoot: weightedFrontFoot,
        pricePerLot: totalGrossProceeds / totalLots,
        lotCount: totalLots,
        totalGrossProceeds,
    };
};

const aggregateAnnualLotTakedown = (
    salesSchedule: ILdDispositionSheetParams['salesSchedule'],
) => {
    const periods = salesSchedule?.periods ?? [];
    const byYear = new Map<number, { lotsSold: number; revenue: number }>();

    periods.forEach(period => {
        const dateStr = period.periodStartDate;
        if (!dateStr) return;
        const year = new Date(dateStr).getUTCFullYear();
        const existing = byYear.get(year) ?? { lotsSold: 0, revenue: 0 };
        byYear.set(year, {
            lotsSold: existing.lotsSold + (period.lotsSold ?? 0),
            revenue: existing.revenue + (period.revenue ?? 0),
        });
    });

    return Array.from(byYear.entries())
        .sort(([a], [b]) => a - b)
        .map(([year, data]) => ({ year, ...data }));
};

export const formatLdDispositionSheet = ({
    dealName,
    proForma,
    disposition,
    residentialLotMixes,
    otherRevenue,
    salesSchedule,
    returns,
    usesSummary,
    showCents = false,
}: ILdDispositionSheetParams): IExcelReportData => {
    const labels = LD_LABELS.disposition;
    const currencyFmt = showCents ? EXCEL_NUM_FORMATS.CURRENCY_CENTS : EXCEL_NUM_FORMATS.CURRENCY;
    const today = new Date().toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
    });

    const mixes = residentialLotMixes?.residentialLotMixes ?? [];
    const otherRevenueRows = otherRevenue?.otherRevenues ?? [];
    const otherRevenueSubtotal = otherRevenueRows.reduce(
        (sum, row) => sum + (parseNumber(row.amount) ?? 0),
        0,
    );

    const grossSaleProceeds = parseNumber(returns.totalSaleProceeds) ?? 0;
    const closingCostsPct = disposition?.closingCostsPercentage ?? 0;
    const closingCosts = parseNumber(returns.closingCosts)
        ?? (grossSaleProceeds * closingCostsPct);
    const netSaleProceeds = parseNumber(returns.netSaleProceedsAfterClosingCosts)
        ?? (grossSaleProceeds - closingCosts);
    const totalOtherRevenue = parseNumber(returns.totalOtherRevenue) ?? otherRevenueSubtotal;
    const totalNetRevenue = netSaleProceeds + totalOtherRevenue;

    const recurringLabel = disposition?.saleFrequency === 'monthly'
        ? `${labels.recurring} / Mo`
        : `${labels.recurring} / Qtr`;
    const salesStartDate = formatLdSalesStart(
        proForma,
        disposition,
        usesSummary?.developmentEndDate,
        true,
    );

    const topHeaderRows: ITableData[] = [
        createDataRow([{ value: dealName, cellFont: { bold: true, size: 14 }, width: 30 }]),
        createDataRow([{ value: LD_EXCEL_SHEET_NAMES.DISPOSITION, cellFont: { bold: true, size: 14 }, width: 30 }]),
        createDataRow([{ value: today, cellFont: { bold: true, size: 14 }, width: 30 }]),
        createBlankRow(DISPOSITION_WIDE_COL_COUNT),
    ];

    const assumptionsRows: ITableData[] = [
        createSectionHeader(labels.salesAssumptions, 2),
        createLabelValueRow(
            labels.salesStart,
            formatLdSalesStart(proForma, disposition, usesSummary?.developmentEndDate, true),
            true,
        ),
        createLabelValueRow(labels.closingCostsPercent, closingCostsPct, true, EXCEL_NUM_FORMATS.PERCENT),
        createLabelValueRow(
            labels.priceEscalation,
            disposition?.annualPriceEscalationPercentage,
            true,
            EXCEL_NUM_FORMATS.PERCENT,
        ),
        createLabelValueRow(labels.saleFrequency, formatSaleFrequency(disposition?.saleFrequency), true),
        createBlankRow(DISPOSITION_WIDE_COL_COUNT),
    ];

    const pricingHeader = createHeaderRow(
        [
            labels.lotType,
            labels.lotSize,
            labels.numberOfLots,
            labels.costPerFrontFoot,
            labels.pricePerLot,
            labels.grossProceeds,
        ],
        EXCEL_COLORS.BLUE_HEADER,
        true,
    );
    const pricingRows: ITableData[] = mixes.map(mix => {
        const pricePerLot = parseNumber(mix.pricePerLot) ?? 0;
        return createDataRow([
            { value: formatLotName(mix.lotName, mix.lotWidth), width: 30 },
            { value: mix.lotWidth, width: 12, alignment: { horizontal: 'right' }, numFmt: EXCEL_NUM_FORMATS.NUMBER },
            { value: mix.lotCount, width: 12, alignment: { horizontal: 'right' }, numFmt: EXCEL_NUM_FORMATS.NUMBER },
            {
                value: parseNumber(mix.costPerFrontFoot) ?? 0,
                width: 15,
                alignment: { horizontal: 'right' },
                numFmt: currencyFmt,
            },
            {
                value: pricePerLot,
                width: 15,
                alignment: { horizontal: 'right' },
                numFmt: currencyFmt,
            },
            {
                value: pricePerLot * mix.lotCount,
                width: 15,
                alignment: { horizontal: 'right' },
                numFmt: currencyFmt,
            },
        ]);
    });

    const blended = computeBlendedPricing(mixes);
    if (mixes.length > 0) {
        pricingRows.push({
            ...createDataRow([
                { value: labels.blended, width: 30, cellFont: { bold: true } },
                { value: '', width: 12 },
                {
                    value: blended.lotCount,
                    width: 12,
                    alignment: { horizontal: 'right' },
                    numFmt: EXCEL_NUM_FORMATS.NUMBER,
                    cellFont: { bold: true },
                },
                {
                    value: blended.costPerFrontFoot ?? 0,
                    width: 15,
                    alignment: { horizontal: 'right' },
                    numFmt: currencyFmt,
                    cellFont: { bold: true },
                },
                {
                    value: blended.pricePerLot ?? 0,
                    width: 15,
                    alignment: { horizontal: 'right' },
                    numFmt: currencyFmt,
                    cellFont: { bold: true },
                },
                {
                    value: blended.totalGrossProceeds,
                    width: 15,
                    alignment: { horizontal: 'right' },
                    numFmt: currencyFmt,
                    cellFont: { bold: true },
                },
            ], true),
            rowFill: {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: EXCEL_COLORS.GRAY_HEADER },
            },
        });
    }

    const takedownHeader = createHeaderRow(
        [labels.lotType, labels.firstSale, labels.initialTake, recurringLabel],
        EXCEL_COLORS.BLUE_HEADER,
        true,
    );
    const takedownRows: ITableData[] = mixes.map(mix => createDataRow([
        { value: formatLotName(mix.lotName, mix.lotWidth), width: 30 },
        { value: salesStartDate, width: 18, alignment: { horizontal: 'center' } },
        { value: mix.initialTake ?? 0, width: 12, alignment: { horizontal: 'right' }, numFmt: EXCEL_NUM_FORMATS.NUMBER },
        { value: mix.recurringNum ?? 0, width: 12, alignment: { horizontal: 'right' }, numFmt: EXCEL_NUM_FORMATS.NUMBER },
    ]));

    const otherRevenueHeader = createHeaderRow(
        [labels.description, labels.date, '', '', '', labels.amount],
        EXCEL_COLORS.BLUE_HEADER,
        true,
    );
    const otherRevenueTableRows: ITableData[] = otherRevenueRows.map(row => createDataRow([
        { value: row.description ?? '', width: 30 },
        { value: formatDate(row.date, true), width: 15 },
        ...createWideSpacerCells(3),
        createWideAmountCell(parseNumber(row.amount) ?? 0, currencyFmt),
    ]));
    otherRevenueTableRows.push(createDataRow([
        { value: labels.otherRevenueSubtotal, width: 30, cellFont: { bold: true } },
        { value: '', width: 15 },
        ...createWideSpacerCells(3),
        createWideAmountCell(otherRevenueSubtotal, currencyFmt, true),
    ], true));

    const revenueSummaryRows: ITableData[] = [
        {
            ...createDataRow([
                { value: labels.revenueSummary, width: 30, cellFont: { bold: true } },
                ...createWideSpacerCells(3),
                {
                    value: labels.revenueSummaryAmount,
                    width: 18,
                    cellFont: { bold: true },
                    alignment: { horizontal: 'center' },
                },
            ], true),
            rowFill: {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: EXCEL_COLORS.DARK_GRAY },
            },
        },
        createDataRow([
            { value: labels.grossSaleProceeds, width: 30 },
            ...createWideSpacerCells(3),
            createWideAmountCell(grossSaleProceeds, currencyFmt),
        ]),
        createDataRow([
            { value: labels.closingCosts, width: 30 },
            ...createWideSpacerCells(3),
            createWideAmountCell(-closingCosts, currencyFmt),
        ]),
        createDataRow([
            { value: labels.netSaleProceeds, width: 30, cellFont: { bold: true } },
            ...createWideSpacerCells(3),
            createWideAmountCell(netSaleProceeds, currencyFmt, true),
        ]),
        createDataRow([
            { value: labels.otherRevenueLine, width: 30 },
            ...createWideSpacerCells(3),
            createWideAmountCell(totalOtherRevenue, currencyFmt),
        ]),
        {
            ...createDataRow([
                { value: labels.totalNetRevenue, width: 30, cellFont: { bold: true } },
                ...createWideSpacerCells(3),
                createWideAmountCell(totalNetRevenue, currencyFmt, true),
            ], true),
            rowFill: {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: EXCEL_COLORS.GRAY_HEADER },
            },
        },
    ];

    const annualTakedown = aggregateAnnualLotTakedown(salesSchedule);
    const annualTakedownHeader = createHeaderRow(
        [labels.annualLotTakedown, ...annualTakedown.map(row => row.year)],
        EXCEL_COLORS.BLUE_HEADER,
        true,
    );
    const annualTakedownRows: ITableData[] = [
        createDataRow([
            { value: labels.lotsSold, width: 30 },
            ...annualTakedown.map(row => ({
                value: row.lotsSold,
                width: 15,
                alignment: { horizontal: 'right' as const },
                numFmt: EXCEL_NUM_FORMATS.NUMBER,
            })),
        ]),
        createDataRow([
            { value: labels.annualGrossSaleProceeds, width: 30 },
            ...annualTakedown.map(row => ({
                value: row.revenue,
                width: 15,
                alignment: { horizontal: 'right' as const },
                numFmt: currencyFmt,
            })),
        ]),
    ];

    return {
        topHeaderRows,
        reportTables: [
            { tableHeaderRows: [], tableData: assumptionsRows },
            {
                tableTitleRows: [createBlankRow(DISPOSITION_WIDE_COL_COUNT)],
                tableHeaderRows: [
                    createSectionHeader(labels.pricingByLotType, DISPOSITION_PRICING_COL_COUNT),
                    pricingHeader,
                ],
                tableData: pricingRows,
            },
            {
                tableTitleRows: [createBlankRow(DISPOSITION_WIDE_COL_COUNT)],
                tableHeaderRows: [
                    createSectionHeader(labels.lotTakedownByType, DISPOSITION_TAKEDOWN_COL_COUNT),
                    takedownHeader,
                ],
                tableData: takedownRows,
            },
            {
                tableTitleRows: [createBlankRow(DISPOSITION_WIDE_COL_COUNT)],
                tableHeaderRows: [
                    createSectionHeader(labels.otherRevenue, DISPOSITION_WIDE_COL_COUNT),
                    otherRevenueHeader,
                ],
                tableData: otherRevenueTableRows,
            },
            {
                tableTitleRows: [createBlankRow(2)],
                tableHeaderRows: [],
                tableData: revenueSummaryRows,
            },
            {
                tableTitleRows: [createBlankRow(DISPOSITION_WIDE_COL_COUNT)],
                tableHeaderRows: [annualTakedownHeader],
                tableData: annualTakedownRows,
            },
        ],
    };
};
